import { useCallback, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SchoolApi } from './client';
import { emitAiLimit, isAiLimit } from '../components/ai/aiLimitEvents';
import type {
  ApproveJoinRequestBody, CoachScope, CoachStreamEvent, CoachReport, CoachLanguage, CycleStatus, CycleCreatePayload, ExamDelivery, PaperDraftCreatePayload, PaperDraftPatchPayload, SheetPatchPayload, QuestionPayload, MarkingStatus, PaperWritten,
  GuardianInput, GroupingLens, StudentGroupWriteBody,
} from './types';

export const Keys = {
  aiWallet: ['school', 'ai-wallet'] as const,
  students: (params: object = {}) => ['school', 'students', params] as const,
  studentTab: (id: number, tab: string) => ['school', 'student', id, tab] as const,
  alertsInbox: (params: object = {}) => ['school', 'alerts', params] as const,
  cases: ['school', 'cases'] as const,
  caseDetail: (id: number) => ['school', 'case', id] as const,
  notes: (studentId?: number) => ['school', 'notes', studentId ?? 'all'] as const,
  teacherAssignments: ['school', 'teacher_assignments'] as const,
  sections: (grade: number) => ['school', 'sections', grade] as const,
  sectionDetail: (id: number) => ['school', 'section', id] as const,
  sectionRoster: (id: number) => ['school', 'section', id, 'roster'] as const,
  classPerfOverview:    (p: object) => ['school', 'class-perf', 'overview', p] as const,
  classPerfBySubject:   (p: object) => ['school', 'class-perf', 'by-subject', p] as const,
  classPerfExams:       (p: object) => ['school', 'class-perf', 'exams', p] as const,
  classPerfTrends:      (p: object) => ['school', 'class-perf', 'trends', p] as const,
  classPerfLeaderboard: (p: object) => ['school', 'class-perf', 'leaderboard', p] as const,
  classPerfChapterMastery: (p: object) => ['school', 'class-perf', 'chapter-mastery', p] as const,
  classPerfQuestions:      (p: object) => ['school', 'class-perf', 'questions', p] as const,
  classPerfSyllabus:       (p: object) => ['school', 'class-perf', 'syllabus', p] as const,
  syllabusEntries:         (p: object) => ['school', 'syllabus-entries', p] as const,
  studentWeeklyTrend:     (id: number, p: object) => ['school', 'student', id, 'weekly-trend', p] as const,
  studentChapterMastery:  (id: number, p: object) => ['school', 'student', id, 'chapter-mastery', p] as const,
  studentMissedQuestions: (id: number, p: object) => ['school', 'student', id, 'missed-questions', p] as const,
  sectionAutoGroups:         (sectionId: number, p: object) => ['school', 'section', sectionId, 'auto-groups', p] as const,
  /** Without a subject this is also the prefix of every subject-filtered list of the section. */
  studentGroupsList:         (sectionId: number, subject?: number | null) =>
    (subject == null ? ['school', 'section', sectionId, 'student-groups'] : ['school', 'section', sectionId, 'student-groups', subject]) as readonly unknown[],
  groupingSuggestions:       (p: object) => ['school', 'class-perf', 'grouping', p] as const,
  assignableGroups:          (grade: number, subject: number) => ['school', 'assignable-groups', grade, subject] as const,
  sectionAlertOverridesList: (sectionId: number) => ['school', 'section', sectionId, 'alert-overrides'] as const,
  coachThreads:  (scope: CoachScope) => ['school', 'coach', 'threads', scope.type, scope.id ?? null] as const,
  coachMessages: (threadId: number)  => ['school', 'coach', 'thread', threadId, 'messages'] as const,
  coachStarters: (scope: CoachScope) => ['school', 'coach', 'starters', scope.type, scope.id ?? null] as const,
  coachAudit:    (params: object)    => ['school', 'coach', 'audit', params] as const,
  parentDigests: (week?: string) => ['school', 'parent-digests', week ?? 'latest'] as const,
  examReport:   (examId: number, p: object) => ['school', 'exam-report', examId, p] as const,
  cycles:       (p: object) => ['school', 'cycles', p] as const,
  cycle:        (id: number) => ['school', 'cycle', id] as const,
  cycleGain:    (id: number, live: boolean) => ['school', 'cycle', id, 'gain', live] as const,
  studentCycles:(id: number) => ['school', 'student', id, 'cycles'] as const,
  paperExamsList: ['school', 'paper-exams'] as const,
  rollNumbers:  (gradeId: number) => ['school', 'roll-numbers', gradeId] as const,
  paperDrafts:  (p: object = {}) => ['school', 'paper-drafts', p] as const,
  paperDraft:   (id: number) => ['school', 'paper-draft', id] as const,
  sheets:       (examId: number, status?: string) => ['school', 'exam', examId, 'sheets', status ?? 'all'] as const,
  sheet:        (examId: number, scanId: number) => ['school', 'exam', examId, 'sheet', scanId] as const,
};

export function useDashboardAcademic(weeks: number) {
  return useQuery({
    queryKey: ['school', 'dashboard', 'academic', { weeks }] as const,
    queryFn: () => SchoolApi.dashboardAcademic({ weeks }),
  });
}

export function useAnalyticsAcademic(
  grade: number | null,
  subject: number | null,
  weeks: number,
) {
  return useQuery({
    queryKey: ['school', 'analytics-academic', grade, subject, weeks] as const,
    queryFn: () => SchoolApi.analyticsAcademic({ grade: grade as number, subject: subject as number, weeks }),
    enabled: grade != null && subject != null,
    retry: false,
  });
}

export function useStudentsList(
  params: { grade?: number; section?: number | 'none' } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: Keys.students(params),
    queryFn: () => SchoolApi.studentsList(params),
    enabled: options.enabled ?? true,
  });
}

export function useStudentOverview(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'overview'),
    queryFn: () => SchoolApi.studentOverview(id),
    enabled: !!id,
  });
}

export function useStudentAcademic(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'academic'),
    queryFn: () => SchoolApi.studentAcademic(id),
    enabled: !!id,
  });
}

export function useStudentCommitments(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'commitments'),
    queryFn: () => SchoolApi.studentCommitments(id),
    enabled: !!id,
  });
}

export function useStudentWellbeing(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'wellbeing'),
    queryFn: () => SchoolApi.studentWellbeing(id),
    enabled: !!id,
  });
}

export function useStudentCases(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'cases'),
    queryFn: () => SchoolApi.studentCases(id),
    enabled: !!id,
  });
}

export function useStudentWeeklyTrend(id: number, params: { subject?: number; weeks?: number; scope?: string }) {
  return useQuery({
    queryKey: Keys.studentWeeklyTrend(id, params),
    queryFn: () => SchoolApi.studentWeeklyTrend(id, params),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useStudentChapterMastery(id: number, params: { subject: number | 'all'; weeks?: number }) {
  return useQuery({
    queryKey: Keys.studentChapterMastery(id, params),
    queryFn: () => SchoolApi.studentChapterMastery(id, {
      ...(params.subject !== 'all' ? { subject: params.subject as number } : {}),
      weeks: params.weeks,
    }),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useStudentMissedQuestions(id: number, params: { subject?: number | 'all'; weeks?: number; limit?: number; min_attempts?: number }) {
  return useQuery({
    queryKey: Keys.studentMissedQuestions(id, params),
    queryFn: () => SchoolApi.studentMissedQuestions(id, {
      ...(params.subject !== 'all' && params.subject != null
        ? { subject: params.subject as number } : {}),
      weeks: params.weeks, limit: params.limit, min_attempts: params.min_attempts,
    }),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useAlertsInbox(params: { severity?: string; rule?: string; status?: string; grade?: number; section?: number | 'none' } = {}) {
  return useQuery({
    queryKey: Keys.alertsInbox(params),
    queryFn: () => SchoolApi.alertsInbox(params),
    refetchInterval: 60_000, // poll every 60s, spec §5.5
  });
}

/** One alert with its detail-only fields (`notified`). Absent fields are fine: the backend may not send them yet. */
export function useAlertDetail(id: number | null | undefined) {
  return useQuery({
    queryKey: ['school', 'alerts', 'detail', id] as const,
    queryFn: () => SchoolApi.alertDetail(id as number),
    enabled: id != null,
    retry: false,
  });
}

export function useAiWallet() {
  return useQuery({ queryKey: Keys.aiWallet, queryFn: () => SchoolApi.aiWallet() });
}

function useAiWalletMutation<TVars, TData>(fn: (v: TVars) => Promise<TData>, opts: { gcTime?: number } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    ...opts,
    onSuccess: () => { qc.invalidateQueries({ queryKey: Keys.aiWallet }); },
  });
}

export const useAiWalletUpdate = () => useAiWalletMutation((b: { use_own_key_first: boolean }) => SchoolApi.aiWalletUpdate(b));
export const useAiKeySave = () => useAiWalletMutation((key: string) => SchoolApi.aiKeySave(key), { gcTime: 0 }); // never keep the plaintext key in the mutation cache
export const useAiKeyRemove = () => useAiWalletMutation(() => SchoolApi.aiKeyRemove());
export const useAiCheckout = () => useAiWalletMutation((b: { bundle_id: string; return_url: string }) => SchoolApi.aiCheckout(b));
export const useAiVerify = () => useAiWalletMutation((tx_ref: string) => SchoolApi.aiVerify(tx_ref));

export function useAlertAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, body }: {
      id: number; action: 'acknowledge' | 'snooze' | 'dismiss' | 'resolve';
      body?: Record<string, unknown>;
    }) => SchoolApi.alertAction(id, action, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school', 'alerts'] });
      qc.invalidateQueries({ queryKey: ['school', 'students'] });
    },
  });
}

export function useCasesList() {
  return useQuery({ queryKey: Keys.cases, queryFn: SchoolApi.casesList });
}

export function useCaseDetail(id: number) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: Keys.caseDetail(id),
    queryFn: () => SchoolApi.caseDetail(id),
    enabled: !!id,
    // A 404 will not heal on retry, so surface "Case not found" at once. Other errors follow the app default.
    retry: (n: number, e: any) => {
      if (e?.response?.status === 404) return false;
      const d = qc.getDefaultOptions().queries?.retry;
      return typeof d === 'function' ? d(n, e) : typeof d === 'number' ? n < d : d === undefined ? n < 3 : !!d;
    },
  });
}

export function useCaseCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: SchoolApi.caseCreate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'cases'] }),
  });
}

export function useCaseTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { to: string; outcome?: string; outcome_note?: string } }) =>
      SchoolApi.caseTransition(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: Keys.caseDetail(vars.id) });
      qc.invalidateQueries({ queryKey: ['school', 'cases'] });
      // An alert links only to an unresolved case: refresh its case_id.
      qc.invalidateQueries({ queryKey: ['school', 'alerts'] });
    },
  });
}

export function useCaseEventCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, body }: {
      caseId: number;
      body: { kind: string; body?: string; payload?: Record<string, unknown> };
    }) => SchoolApi.caseEventCreate(caseId, body),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: Keys.caseDetail(vars.caseId) }),
  });
}

export function useCaseNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, body }: { caseId: number; body: { title: string; body: string } }) =>
      SchoolApi.caseNudge(caseId, body),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: Keys.caseDetail(vars.caseId) }),
  });
}

export function useNotesForStudent(studentId: number) {
  return useQuery({
    queryKey: Keys.notes(studentId),
    queryFn: () => SchoolApi.notesList(studentId),
    enabled: !!studentId,
  });
}

export function useNoteCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: SchoolApi.noteCreate,
    onSuccess: (n) => qc.invalidateQueries({ queryKey: Keys.notes(n.student) }),
  });
}

export function useNoteDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: SchoolApi.noteDelete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'notes'] }),
  });
}

export function useTeacherAssignments(options: { enabled?: boolean; retry?: boolean } = {}) {
  return useQuery({
    queryKey: Keys.teacherAssignments,
    queryFn: SchoolApi.teacherAssignmentsList,
    enabled: options.enabled ?? true,
    ...(options.retry === false ? { retry: false } : {}),
  });
}

export function useTeacherAssignmentCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: SchoolApi.teacherAssignmentCreate,
    onSuccess: () => qc.invalidateQueries({ queryKey: Keys.teacherAssignments }),
  });
}

export function useTeacherAssignmentDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: SchoolApi.teacherAssignmentDelete,
    onSuccess: () => qc.invalidateQueries({ queryKey: Keys.teacherAssignments }),
  });
}

export function useStudentEngagement(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'engagement'),
    queryFn: () => SchoolApi.studentEngagement(id),
    enabled: !!id,
  });
}

export function useStudentInterests(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'interests'),
    queryFn: () => SchoolApi.studentInterests(id),
    enabled: !!id,
  });
}

export function useAlertRulesList() {
  return useQuery({
    queryKey: ['school', 'alert-rules'],
    queryFn: () => SchoolApi.alertRulesList(),
  });
}

export function useStudentViewers(studentId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['school', 'student', studentId, 'viewers'],
    queryFn: () => SchoolApi.studentViewers(studentId),
    enabled: !!studentId && enabled,
  });
}

// Phase 2.5-A Sections (spec §6)
export function useSectionsList(grade: number | null) {
  return useQuery({
    queryKey: Keys.sections(grade ?? 0),
    queryFn: () => SchoolApi.sectionsList({ grade: grade as number }),
    enabled: grade != null,
  });
}

export function useSectionDetail(id: number | null) {
  return useQuery({
    queryKey: Keys.sectionDetail(id ?? 0),
    queryFn: () => SchoolApi.sectionDetail(id as number),
    enabled: id != null,
  });
}

export function useSectionRoster(id: number | null) {
  return useQuery({
    queryKey: Keys.sectionRoster(id ?? 0),
    queryFn: () => SchoolApi.sectionRoster(id as number),
    enabled: id != null,
  });
}

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { school_grade: number; name: string }) =>
      SchoolApi.sectionCreate(body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['school', 'sections', vars.school_grade] });
    },
  });
}

export function useRenameSection(grade: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      SchoolApi.sectionUpdate(id, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'sections', grade] }),
  });
}

export function useDeleteSection(grade: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => SchoolApi.sectionDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'sections', grade] }),
  });
}

export function useAddStudentsToSection(sectionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { student_ids: number[] }) =>
      SchoolApi.sectionAddStudents(sectionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: Keys.sectionRoster(sectionId) });
      qc.invalidateQueries({ queryKey: ['school', 'students'] });
    },
  });
}

export function useRemoveStudentsFromSection(sectionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { student_ids: number[] }) =>
      SchoolApi.sectionRemoveStudents(sectionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: Keys.sectionRoster(sectionId) });
      qc.invalidateQueries({ queryKey: ['school', 'students'] });
    },
  });
}

export function useBulkAssignSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { student_ids: number[]; section_id: number | null }) =>
      SchoolApi.bulkAssignSection(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school', 'students'] });
      qc.invalidateQueries({ queryKey: ['school', 'sections'] });
    },
  });
}

export function useClassPerfOverview(params: { grade: number | null; section: number | 'all'; subject: number | 'all'; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfOverview(params),
    queryFn: () => SchoolApi.classPerfOverview({
      grade: params.grade as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
      ...(params.subject !== 'all' ? { subject: params.subject } : {}),
      weeks: params.weeks,
    }),
    enabled: params.grade != null,
  });
}

export function useClassPerfBySubject(params: { grade: number | null; section: number | 'all' }) {
  return useQuery({
    queryKey: Keys.classPerfBySubject(params),
    queryFn: () => SchoolApi.classPerfBySubject({
      grade: params.grade as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
    }),
    enabled: params.grade != null,
  });
}

export function useClassPerfExams(params: { grade: number | null; section: number | 'all'; subject: number | 'all'; scopes: string[]; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfExams(params),
    queryFn: () => SchoolApi.classPerfExams({
      grade: params.grade as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
      ...(params.subject !== 'all' ? { subject: params.subject } : {}),
      scope: params.scopes.join(','),
      weeks: params.weeks,
    }),
    enabled: params.grade != null && params.scopes.length > 0,
  });
}

export function useClassPerfTrends(params: { grade: number | null; section: number | 'all'; subject: number | 'all'; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfTrends(params),
    queryFn: () => SchoolApi.classPerfTrends({
      grade: params.grade as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
      ...(params.subject !== 'all' ? { subject: params.subject } : {}),
      weeks: params.weeks,
    }),
    enabled: params.grade != null,
  });
}

export function useClassPerfLeaderboard(params: { grade: number | null; subject: number | 'all'; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfLeaderboard(params),
    queryFn: () => SchoolApi.classPerfLeaderboard({
      grade: params.grade as number,
      subject: params.subject as number,
      weeks: params.weeks,
    }),
    enabled: params.grade != null && params.subject !== 'all',
  });
}

/**
 * Sections of a grade ranked on the grade-level "all subjects" rollup (`subject=all`).
 * An older backend answers 400 here; callers treat an error as "rank unavailable".
 */
export function useClassPerfSectionRanks(params: { grade: number | null; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfLeaderboard({ ...params, subject: 'all' }),
    queryFn: () => SchoolApi.classPerfLeaderboard({ grade: params.grade as number, subject: 'all', weeks: params.weeks }),
    enabled: params.grade != null,
    retry: false,
  });
}

export function useClassPerfChapterMastery(params: { grade: number | null; subject: number | 'all'; section: number | 'all'; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfChapterMastery(params),
    queryFn: () => SchoolApi.classPerfChapterMastery({
      grade: params.grade as number,
      subject: params.subject as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
      weeks: params.weeks,
    }),
    enabled: params.grade != null && params.subject !== 'all',
  });
}

export function useClassPerfQuestions(params: { grade: number | null; section: number | 'all'; subject: number | 'all'; weeks?: number; limit?: number; min_attempts?: number }) {
  return useQuery({
    queryKey: Keys.classPerfQuestions(params),
    queryFn: () => SchoolApi.classPerfQuestions({
      grade: params.grade as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
      ...(params.subject !== 'all' ? { subject: params.subject } : {}),
      weeks: params.weeks,
      limit: params.limit,
      min_attempts: params.min_attempts,
    }),
    enabled: params.grade != null,
  });
}

export function useClassPerfSyllabus(params: { grade: number | null; section: number | 'all'; weeks?: number }) {
  return useQuery({
    queryKey: Keys.classPerfSyllabus(params),
    queryFn: () => SchoolApi.classPerfSyllabus({
      grade: params.grade as number,
      section: params.section as number,
      weeks: params.weeks,
    }),
    enabled: params.grade != null && params.section !== 'all',
  });
}

export function useCreateSyllabusEntry(_invalidateParams: { grade: number; section: number; weeks?: number }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { section: number; subject: number; chapter: number; week_start: string; notes?: string }) =>
      SchoolApi.syllabusEntryCreate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school', 'class-perf', 'syllabus'] });
    },
  });
}

export function useUpdateSyllabusEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { notes?: string } }) =>
      SchoolApi.syllabusEntryUpdate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'class-perf', 'syllabus'] }),
  });
}

export function useDeleteSyllabusEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => SchoolApi.syllabusEntryDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'class-perf', 'syllabus'] }),
  });
}

// Phase 2.5-E grouping + section alert overrides

export function useSectionAutoGroups(sectionId: number, params: { weeks?: number; threshold?: number }) {
  return useQuery({
    queryKey: Keys.sectionAutoGroups(sectionId, params),
    queryFn: () => SchoolApi.sectionAutoGroups(sectionId, params),
    enabled: Number.isFinite(sectionId) && sectionId > 0,
  });
}

export function useStudentGroupsList(sectionId: number, subject?: number | null) {
  return useQuery({
    queryKey: Keys.studentGroupsList(sectionId, subject),
    queryFn: () => SchoolApi.studentGroupsList(sectionId, subject),
    enabled: Number.isFinite(sectionId) && sectionId > 0,
  });
}

/** Saved groups changed: every list of the section and the assign pickers. */
function invalidateGroups(qc: ReturnType<typeof useQueryClient>, sectionId: number) {
  qc.invalidateQueries({ queryKey: Keys.studentGroupsList(sectionId) });
  qc.invalidateQueries({ queryKey: ['school', 'assignable-groups'] });
}

export function useCreateStudentGroup(sectionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StudentGroupWriteBody) =>
      SchoolApi.studentGroupCreate(sectionId, body),
    onSuccess: () => invalidateGroups(qc, sectionId),
  });
}

export function useUpdateStudentGroup(sectionId: number, groupId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<StudentGroupWriteBody>) =>
      SchoolApi.studentGroupUpdate(sectionId, groupId, body),
    onSuccess: () => invalidateGroups(qc, sectionId),
  });
}

export function useDeleteStudentGroup(sectionId: number, groupId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.studentGroupDelete(sectionId, groupId),
    onSuccess: () => invalidateGroups(qc, sectionId),
  });
}

export function useRefreshStudentGroup(sectionId: number, groupId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.studentGroupRefresh(sectionId, groupId),
    onSuccess: () => invalidateGroups(qc, sectionId),
  });
}

/** Teacher grouping suggestions (one section + one subject). */
export function useGroupingSuggestions(p: { grade: number; section: number | 'all'; subject: number | 'all'; lens: GroupingLens; chapter?: number; weeks?: number }) {
  const ready = typeof p.section === 'number' && typeof p.subject === 'number';
  const params = { grade: p.grade, section: p.section as number, subject: p.subject as number, lens: p.lens,
    ...(p.chapter != null ? { chapter: p.chapter } : {}), ...(p.weeks != null ? { weeks: p.weeks } : {}) };
  return useQuery({
    queryKey: Keys.groupingSuggestions(params),
    queryFn: () => SchoolApi.groupingSuggestions(params),
    enabled: ready && p.grade > 0,
  });
}

/** Saved groups the viewer may give work of this grade + subject to. */
export function useAssignableGroups(grade: number | null | undefined, subject: number | null | undefined) {
  return useQuery({
    queryKey: Keys.assignableGroups(grade ?? 0, subject ?? 0),
    queryFn: () => SchoolApi.assignableGroups({ grade: grade!, subject: subject! }),
    enabled: !!grade && grade > 0 && !!subject && subject > 0,
  });
}

export function useSectionAlertOverridesList(sectionId: number) {
  return useQuery({
    queryKey: Keys.sectionAlertOverridesList(sectionId),
    queryFn: () => SchoolApi.sectionAlertOverridesList(sectionId),
    enabled: Number.isFinite(sectionId) && sectionId > 0,
  });
}

export function useUpsertSectionAlertOverride(sectionId: number, ruleCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { is_enabled: boolean | null; threshold_config_override: Record<string, unknown> }) =>
      SchoolApi.sectionAlertOverrideUpsert(sectionId, ruleCode, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: Keys.sectionAlertOverridesList(sectionId) }),
  });
}

export function useDeleteSectionAlertOverride(sectionId: number, ruleCode: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.sectionAlertOverrideDelete(sectionId, ruleCode),
    onSuccess: () => qc.invalidateQueries({ queryKey: Keys.sectionAlertOverridesList(sectionId) }),
  });
}

const coachScopeReady = (scope: CoachScope) => scope.type === 'school' || scope.type === 'mine' || !!scope.id;

export function useCoachThreads(scope: CoachScope) {
  return useQuery({
    queryKey: Keys.coachThreads(scope),
    queryFn: () => SchoolApi.coachThreads(scope),
    enabled: coachScopeReady(scope),
  });
}

/** A thread's messages, newest page first; `fetchNextPage` loads older ones. */
export function useCoachMessages(threadId: number | null) {
  return useInfiniteQuery({
    queryKey: threadId === null ? ['school', 'coach', 'thread', 'null'] : Keys.coachMessages(threadId),
    queryFn: ({ pageParam }) => SchoolApi.coachMessages(threadId as number, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.has_more && last.messages.length ? last.messages[0].id : undefined),
    enabled: threadId !== null,
  });
}

export function useCoachStarters(scope: CoachScope, enabled = true) {
  return useQuery({
    queryKey: Keys.coachStarters(scope),
    queryFn: () => SchoolApi.coachStarters(scope),
    enabled: enabled && coachScopeReady(scope),
    staleTime: 5 * 60_000,
  });
}

export function useCreateCoachThread(scope: CoachScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.coachCreateThread(scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: Keys.coachThreads(scope) });
    },
  });
}

export function useSendCoachMessage(scope: CoachScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { threadId: number; content: string }) =>
      SchoolApi.coachSend(vars.threadId, vars.content),
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: Keys.coachMessages(vars.threadId) });
      qc.invalidateQueries({ queryKey: Keys.coachThreads(scope) });
    },
  });
}

export function useUpdateCoachThread(scope: CoachScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { threadId: number; body: { title?: string; archived?: boolean } }) =>
      SchoolApi.coachPatchThread(vars.threadId, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: Keys.coachThreads(scope) });
    },
  });
}

export function useDeleteCoachThread(scope: CoachScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: number) => SchoolApi.coachDeleteThread(threadId),
    onSuccess: () => qc.invalidateQueries({ queryKey: Keys.coachThreads(scope) }),
  });
}

export function useCoachFeedback(threadId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { messageId: number; value: -1 | 0 | 1; note?: string }) =>
      SchoolApi.coachFeedback(threadId as number, vars.messageId, { value: vars.value, note: vars.note }),
    onSuccess: () => { if (threadId !== null) qc.invalidateQueries({ queryKey: Keys.coachMessages(threadId) }); },
  });
}

export function useCoachAudit(params: { page?: number; user?: number; scope_type?: string } = {}) {
  return useQuery({
    queryKey: Keys.coachAudit(params),
    queryFn: () => SchoolApi.coachAudit(params),
    placeholderData: keepPreviousData,
  });
}

// Phase 3B — Parent digest (spec §6.2.10, §7.2 Tab 8)
export function useParentDigestsList(week?: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: Keys.parentDigests(week),
    queryFn: () => SchoolApi.parentDigestsList(week ? { week } : {}),
    enabled: options.enabled ?? true,
    // ParentDigestPage hydrates `week` from the first page's own rows once
    // loaded, which switches the query key from the 'latest' sentinel to a
    // concrete date. Without this, that key change resets the query to
    // isLoading: true and the table flashes back to a spinner even though
    // the previous rows are still valid to show.
    placeholderData: keepPreviousData,
  });
}

export function useParentDigestResend(week?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => SchoolApi.parentDigestResend(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: Keys.parentDigests(week) }),
  });
}

export function useStudentParent(id: number) {
  return useQuery({
    queryKey: Keys.studentTab(id, 'parent'),
    queryFn: () => SchoolApi.studentParent(id),
    enabled: !!id,
  });
}

/** Add, edit or remove one of a student's guardians; refreshes the Parent tab. */
export function useGuardianMutations(studentId: number) {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: Keys.studentTab(studentId, 'parent') });
  return {
    create: useMutation({ mutationFn: (body: GuardianInput) => SchoolApi.guardianCreate(studentId, body), onSuccess }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: GuardianInput }) => SchoolApi.guardianUpdate(studentId, id, body),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: number) => SchoolApi.guardianDelete(studentId, id), onSuccess }),
  };
}

/**
 * One coach turn over the event stream: the text so far, which lookup the coach
 * is making, and an error message. `stop()` aborts the stream (the server keeps
 * what was written, marked interrupted).
 */
export function useCoachTurn(scope: CoachScope) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [tool, setTool] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(
    async (threadId: number, body: { content?: string; report?: CoachReport; language?: CoachLanguage },
           regenerateMessageId?: number): Promise<{ ok: boolean; aborted: boolean }> => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setText(''); setTool(null); setError(null); setPending(true);
      let ok = true;
      let aborted = false;
      try {
        await SchoolApi.coachStream({ threadId, regenerateMessageId }, body, (ev: CoachStreamEvent) => {
          if (ev.type === 'delta') { setTool(null); setText((t) => t + ev.text); }
          else if (ev.type === 'tool') setTool(ev.label);
          else if (ev.type === 'error') {
            ok = false; setError(ev.message);
            if (ev.code === 'ai_limit_reached') {
              const p = isAiLimit(ev.ai_limit) ?? { ai_limit_reached: true as const, detail: ev.message };
              emitAiLimit(p);
            }
          }
          else if (ev.type === 'done') setText(ev.message.content);
        }, ctrl.signal);
      } catch (e) {
        ok = false;
        if ((e as { name?: string })?.name === 'AbortError') aborted = true;
        else setError((e as Error)?.message || 'The coach is unavailable right now.');
      } finally {
        setPending(false);
        setTool(null);
        // A first load still in flight (a new chat) would win over the refetch: cancel it.
        await qc.cancelQueries({ queryKey: Keys.coachMessages(threadId) });
        await qc.invalidateQueries({ queryKey: Keys.coachMessages(threadId) });
        await qc.invalidateQueries({ queryKey: Keys.coachThreads(scope) });
        setText('');
      }
      return { ok, aborted };
    }, [qc, scope]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);

  return { run, stop, text, tool, pending, error, clearError: () => setError(null) };
}

export function useExamReport(examId: number | null, params: { section: number | 'all' }) {
  return useQuery({
    queryKey: Keys.examReport(examId ?? 0, params),
    queryFn: () => SchoolApi.examReport(examId as number, params),
    enabled: examId != null && examId > 0,
  });
}

export function useCycles(params: { grade: number | null; section: number | 'all'; subject: number | 'all'; status?: CycleStatus }) {
  return useQuery({
    queryKey: Keys.cycles(params),
    queryFn: () => SchoolApi.cyclesList({
      grade: params.grade as number,
      ...(params.section !== 'all' ? { section: params.section } : {}),
      ...(params.subject !== 'all' ? { subject: params.subject } : {}),
      ...(params.status ? { status: params.status } : {}),
    }),
    enabled: params.grade != null,
  });
}

export function useCycle(id: number | null) {
  return useQuery({
    queryKey: Keys.cycle(id ?? 0),
    queryFn: () => SchoolApi.cycleDetail(id as number),
    enabled: id != null && id > 0,
  });
}

/**
 * Live gain for one cycle. The cycle payload already carries its last stored
 * gain, so this query only runs once the caller actually asks for a live
 * recompute (`live`) — opening a cycle must not spend a recompute request.
 */
export function useCycleGain(id: number | null, live = false, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: Keys.cycleGain(id ?? 0, live),
    queryFn: () => SchoolApi.cycleGain(id as number, live),
    enabled: (options.enabled ?? live) && id != null && id > 0,
  });
}

export function useStudentCycles(id: number) {
  return useQuery({
    queryKey: Keys.studentCycles(id),
    queryFn: () => SchoolApi.studentCycles(id),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useCreateCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CycleCreatePayload) => SchoolApi.cycleCreate(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['school', 'cycles'] }); },
  });
}

export function useCloseCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => SchoolApi.cycleClose(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['school', 'cycles'] });
      qc.invalidateQueries({ queryKey: Keys.cycle(id) });
    },
  });
}

// ----- paper exams (2026-09-25 plan B, Task 1) -----

const PAPER_POLL_MS = 2_000;
const SHEET_BUSY = new Set(['queued', 'reading']);

/** Every paper exam in scope, for the list page. */
export function usePaperExamsList() {
  return useQuery({
    queryKey: Keys.paperExamsList,
    queryFn: () => SchoolApi.paperExamsList(),
  });
}

export function useRollNumbers(gradeId: number | null) {
  return useQuery({
    queryKey: Keys.rollNumbers(gradeId ?? 0),
    queryFn: () => SchoolApi.rollNumbers(gradeId as number),
    enabled: gradeId != null && gradeId > 0,
  });
}

export function useRollNumbersAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gradeId: number) => SchoolApi.rollNumbersAssign(gradeId),
    onSuccess: (data, gradeId) => {
      qc.setQueryData(Keys.rollNumbers(gradeId), data);
      qc.invalidateQueries({ queryKey: Keys.rollNumbers(gradeId) });
    },
  });
}

export function usePaperDrafts(params: { status?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: Keys.paperDrafts(params),
    queryFn: () => SchoolApi.paperDraftsList(params),
  });
}

/** One draft; polls every 2 s while the extractor is still running. */
export function usePaperDraft(id: number | null) {
  return useQuery({
    queryKey: Keys.paperDraft(id ?? 0),
    queryFn: () => SchoolApi.paperDraftsDetail(id as number),
    enabled: id != null && id > 0,
    refetchInterval: (q) => (q.state.data?.status === 'extracting' ? PAPER_POLL_MS : false),
  });
}

export function usePaperDraftCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PaperDraftCreatePayload) => SchoolApi.paperDraftsCreate(payload),
    onSuccess: (draft) => {
      qc.setQueryData(Keys.paperDraft(draft.id), draft);
      qc.invalidateQueries({ queryKey: ['school', 'paper-drafts'] });
    },
  });
}

export function usePaperDraftPatch(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PaperDraftPatchPayload) => SchoolApi.paperDraftsPatch(id, body),
    onSuccess: (draft) => {
      qc.setQueryData(Keys.paperDraft(id), draft);
      qc.invalidateQueries({ queryKey: ['school', 'paper-drafts'] });
    },
  });
}

export function usePaperDraftSave(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.paperDraftsSave(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: Keys.paperDraft(id) });
      qc.invalidateQueries({ queryKey: ['school', 'paper-drafts'] });
      qc.invalidateQueries({ queryKey: Keys.paperExamsList });
    },
  });
}

export function useExamDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { examId: number; delivery: ExamDelivery }) =>
      SchoolApi.examDelivery(vars.examId, vars.delivery),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['school', 'exam', vars.examId] });
      qc.invalidateQueries({ queryKey: ['school', 'exam-report', vars.examId] });
      qc.invalidateQueries({ queryKey: ['school', 'class-perf', 'exams'] });
      qc.invalidateQueries({ queryKey: Keys.paperExamsList });
    },
  });
}

/** Scans of one exam; polls every 2 s while any scan is queued or being read. */
export function useSheets(examId: number | null, status?: string) {
  return useQuery({
    queryKey: Keys.sheets(examId ?? 0, status),
    queryFn: () => SchoolApi.sheetsList(examId as number, status),
    enabled: examId != null && examId > 0,
    placeholderData: keepPreviousData,
    refetchInterval: (q) =>
      (q.state.data?.scans.some((s) => SHEET_BUSY.has(s.status)) ? PAPER_POLL_MS : false),
  });
}

export function useSheet(examId: number, scanId: number | null) {
  return useQuery({
    queryKey: Keys.sheet(examId, scanId ?? 0),
    queryFn: () => SchoolApi.sheetsDetail(examId, scanId as number),
    enabled: examId > 0 && scanId != null && scanId > 0,
    refetchInterval: (q) => (q.state.data && SHEET_BUSY.has(q.state.data.status) ? PAPER_POLL_MS : false),
  });
}

function invalidateSheets(qc: ReturnType<typeof useQueryClient>, examId: number, scanId?: number) {
  qc.invalidateQueries({ queryKey: ['school', 'exam', examId, 'sheets'] });
  if (scanId != null) qc.invalidateQueries({ queryKey: Keys.sheet(examId, scanId) });
}

/** Switch whether written questions count; confirmed results are re-scored, so every
 * exam figure is refreshed. */
export function useSheetsWritten(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: PaperWritten) => SchoolApi.sheetsWritten(examId, mode),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useSheetsUpload(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => SchoolApi.sheetsUpload(examId, files),
    onSuccess: () => invalidateSheets(qc, examId),
  });
}

export function useSheetPatch(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { scanId: number; body: SheetPatchPayload }) =>
      SchoolApi.sheetsPatch(examId, vars.scanId, vars.body),
    onSuccess: (detail, vars) => {
      qc.setQueryData(Keys.sheet(examId, vars.scanId), detail);
      invalidateSheets(qc, examId, vars.scanId);
    },
  });
}

function useReportMutation<V>(examId: number, fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['school', 'exam-report', examId] }); },
  });
}
export const useReleaseResults = (examId: number) => useReportMutation<void>(examId, () => SchoolApi.releaseResults(examId));
export const useUnreleaseResults = (examId: number) => useReportMutation<void>(examId, () => SchoolApi.unreleaseResults(examId));
export const useSaveResultComment = (examId: number) =>
  useReportMutation<{ studentId: number; body: string }>(examId, (v) => SchoolApi.saveResultComment(examId, v.studentId, v.body));
export const useDeleteResultComment = (examId: number) =>
  useReportMutation<number>(examId, (studentId) => SchoolApi.deleteResultComment(examId, studentId));

export function useSheetConfirm(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { scanId: number; replace?: boolean }) =>
      SchoolApi.sheetsConfirm(examId, vars.scanId, vars.replace ?? false),
    onSuccess: (detail, vars) => {
      qc.setQueryData(Keys.sheet(examId, vars.scanId), detail);
      invalidateSheets(qc, examId, vars.scanId);
      qc.invalidateQueries({ queryKey: ['school', 'exam-report', examId] });
    },
  });
}

export function useSheetsConfirmMatched(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.sheetsConfirmMatched(examId),
    onSuccess: () => {
      invalidateSheets(qc, examId);
      qc.invalidateQueries({ queryKey: ['school', 'exam-report', examId] });
    },
  });
}

export function useSheetReread(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: number | { scanId: number; corners: [number, number][] | null }) =>
      (typeof v === 'number' ? SchoolApi.sheetsReread(examId, v) : SchoolApi.sheetsReread(examId, v.scanId, v.corners)),
    onSuccess: (detail, v) => {
      const scanId = typeof v === 'number' ? v : v.scanId;
      qc.setQueryData(Keys.sheet(examId, scanId), detail);
      invalidateSheets(qc, examId, scanId);
    },
  });
}

export function useApproveJoinRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ApproveJoinRequestBody }) =>
      SchoolApi.approveJoinRequest(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['institutionRequests'] });
      qc.invalidateQueries({ queryKey: ['school', 'students'] });
      qc.invalidateQueries({ queryKey: ['school', 'sections'] });
    },
  });
}

export function useSchoolTasks(filters: { kind?: string; state?: string; grade?: number }) {
  return useInfiniteQuery({
    queryKey: ['school', 'tasks', filters],
    queryFn: ({ pageParam }) => SchoolApi.schoolTasks({ ...filters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (last) => last.next_page ?? undefined,
  });
}
export function useCreateNotice() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: SchoolApi.createNotice,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'tasks'] }) });
}
export function useTaskTracker(id: number | null) {
  return useQuery({
    queryKey: ['school', 'tasks', 'tracker', id],
    queryFn: () => SchoolApi.taskTracker(id as number),
    enabled: id != null,
  });
}
export function useNudgeTask() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: number) => SchoolApi.nudgeTask(id),
    // onSettled: a 409/400 also means the tracker footer is stale.
    onSettled: () => qc.invalidateQueries({ queryKey: ['school', 'tasks'] }) });
}
export function useArchiveTask() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: SchoolApi.archiveTask,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'tasks'] }) });
}

// ---- Worksheets / homework authoring ----
export const authoringKey = (examId: number) => ['school', 'authoring-exam', examId] as const;

export function useAuthoringExam(examId: number) {
  return useQuery({
    queryKey: authoringKey(examId),
    queryFn: () => SchoolApi.authoringExam(examId),
    enabled: Number.isFinite(examId) && examId > 0,
  });
}

/** Every grade's chapters of the worksheet's subject, for the question forms' chapter picker. */
export function useAuthoringChapters(examId: number) {
  return useQuery({
    queryKey: ['school', 'authoring-chapters', examId] as const,
    queryFn: () => SchoolApi.authoringChapters(examId),
    enabled: Number.isFinite(examId) && examId > 0,
    staleTime: 5 * 60_000,
  });
}

/** The subject's books the AI can draw chapters, explanations and references from. */
export function useAnnotateBooks(examId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['school', 'annotate-books', examId] as const,
    queryFn: () => SchoolApi.authoringAnnotateBooks(examId),
    enabled: enabled && Number.isFinite(examId) && examId > 0,
    staleTime: 5 * 60_000,
  });
}

export function useSaveAuthoringQuestion(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { qid?: number; body: QuestionPayload }) =>
      vars.qid ? SchoolApi.authoringQuestionUpdate(examId, vars.qid, vars.body)
        : SchoolApi.authoringQuestionCreate(examId, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: authoringKey(examId) }),
  });
}

export function useDeleteAuthoringQuestion(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (qid: number) => SchoolApi.authoringQuestionDelete(examId, qid),
    onSuccess: () => qc.invalidateQueries({ queryKey: authoringKey(examId) }),
  });
}

export function usePublishAuthoringExam(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => SchoolApi.authoringPublish(examId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: authoringKey(examId) });
      qc.invalidateQueries({ queryKey: ['exams'] });
    },
  });
}

// ---- Attachments (school mode phase 3g) ----
export function useUploadTaskAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: number; files: File[] }) => SchoolApi.uploadTaskAttachments(v.taskId, v.files),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'tasks'] }),
  });
}
export function useDeleteTaskAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { taskId: number; id: number }) => SchoolApi.deleteTaskAttachment(v.taskId, v.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school', 'tasks'] }),
  });
}
export function useUploadExamAttachments(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => SchoolApi.uploadExamAttachments(examId, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: authoringKey(examId) }),
  });
}
export function useDeleteExamAttachment(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => SchoolApi.deleteExamAttachment(examId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: authoringKey(examId) }),
  });
}

// ---- Written-answer marking (school mode phase 3c-i) ----
export const markingKeys = {
  summary: ['school', 'marking-summary'] as const,
  exam: (examId: number) => ['school', 'marking', examId] as const,
  question: (examId: number, qid: number, status: MarkingStatus) => ['school', 'marking', examId, qid, status] as const,
};

export function useMarkingSummary(enabled = true) {
  return useQuery({ queryKey: markingKeys.summary, queryFn: SchoolApi.markingSummary, enabled });
}

export function useExamMarking(examId: number, enabled = true) {
  return useQuery({
    queryKey: markingKeys.exam(examId),
    queryFn: () => SchoolApi.examMarking(examId),
    enabled: enabled && Number.isFinite(examId) && examId > 0,
  });
}

/** Polls every 10 s while any answer still waits for its AI suggestion. */
export function useExamMarkingQuestion(examId: number, qid: number | null, status: MarkingStatus) {
  return useQuery({
    queryKey: markingKeys.question(examId, qid ?? 0, status),
    queryFn: () => SchoolApi.examMarkingQuestion(examId, qid as number, status),
    enabled: Number.isFinite(examId) && examId > 0 && qid != null,
    refetchInterval: (query) => (query.state.data?.answers.some((a) => a.ai_status === 'pending') ? 10000 : false),
  });
}

export function useConfirmWrittenMark(examId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; marks: number }) => SchoolApi.confirmWrittenMark(vars.id, vars.marks),
    // Returned so mutateAsync resolves after the marking lists have refetched.
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: markingKeys.exam(examId) }),
      qc.invalidateQueries({ queryKey: markingKeys.summary }),
      qc.invalidateQueries({ queryKey: ['school', 'exam-report', examId] }),
      qc.invalidateQueries({ queryKey: ['school', 'tasks'] }),
      qc.invalidateQueries({ queryKey: ['exams'] }),
    ]),
  });
}

// ---- Student 360 academics ----
export function useStudentExamBreakdown(id: number, resultId: number | null) {
  return useQuery({
    queryKey: ['school', 'student', id, 'exam-breakdown', resultId] as const,
    queryFn: () => SchoolApi.studentExamBreakdown(id, resultId as number),
    enabled: resultId != null,
  });
}

export function useStudentPatterns(id: number, subject: number | 'all' = 'all') {
  return useQuery({
    queryKey: ['school', 'student', id, 'patterns', subject] as const,
    queryFn: () => SchoolApi.studentPatterns(id, subject),
  });
}

export function useStudentNationalReadiness(id: number, enabled = true) {
  return useQuery({
    queryKey: ['school', 'student', id, 'national-readiness'] as const,
    queryFn: () => SchoolApi.studentNationalReadiness(id),
    enabled,
    staleTime: 10 * 60_000,
  });
}
